/**
 * Login Component
 * 기본 로그인/회원가입 폼
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Clock, Mail, Lock, User, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import ForgotPasswordModal from './ForgotPasswordModal';
import FindEmailModal from './FindEmailModal';

const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 10) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  const levels = [
    { label: '', color: '' },
    { label: '매우 약함', color: 'bg-red-500' },
    { label: '약함', color: 'bg-orange-500' },
    { label: '보통', color: 'bg-yellow-500' },
    { label: '강함', color: 'bg-green-500' },
    { label: '매우 강함', color: 'bg-green-700' },
  ];
  return { score, ...levels[score] };
};

const LoginForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLogin = location.pathname !== '/signup';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: ''
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showFindEmail, setShowFindEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const { signIn, signUp, loading } = useAuth();

  // pathname 변경 시 폼 초기화
  useEffect(() => {
    setFormData({ email: '', password: '', confirmPassword: '', fullName: '' });
    setError('');
    setMessage('');
    setAgreedToTerms(false);
  }, [location.pathname]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 입력 시 에러 메시지 클리어
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError('이메일과 비밀번호를 입력해주세요.');
      return false;
    }

    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        setError('비밀번호가 일치하지 않습니다.');
        return false;
      }
      if (formData.password.length < 6) {
        setError('비밀번호는 6자리 이상이어야 합니다.');
        return false;
      }
      if (!formData.fullName.trim()) {
        setError('이름을 입력해주세요.');
        return false;
      }
      if (!agreedToTerms) {
        setError('이용약관 및 개인정보 처리방침에 동의해주세요.');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setError('');
    setMessage('');

    try {
      if (isLogin) {
        await signIn(formData.email, formData.password);
        // 로그인 성공 시 pendingInviteToken 확인 → 초대 수락 페이지로 이동
        const pendingToken = sessionStorage.getItem('pendingInviteToken');
        if (pendingToken) {
          navigate(`/invite/${pendingToken}`, { replace: true });
          return;
        }
        // 그 외 자동으로 AuthContext에서 처리됨
      } else {
        await signUp(formData.email, formData.password, {
          full_name: formData.fullName
        });
        setMessage('회원가입이 완료되었습니다. 이메일을 확인해주세요.');
        // 회원가입 성공 → 로그인 페이지로 이동
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('Auth error:', error);
      setError(error.message || '로그인에 실패했습니다.');
    }
  };

  const toggleMode = () => {
    navigate(isLogin ? '/signup' : '/login', { replace: true });
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-md px-6 py-12 sm:px-8">
            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{background: 'linear-gradient(135deg, #2563eb, #3b82f6)'}}>
                <Clock className="w-7 h-7 text-white" />
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-1 text-center">
              {isLogin ? '로그인' : '회원가입'}
            </h2>
            <p className="text-gray-500 mb-8 text-center">
              {isLogin ? '계정에 로그인하여 시작하세요' : '새 계정을 만들어보세요'}
            </p>

            <form className="space-y-5" onSubmit={handleSubmit}>
              {!isLogin && (
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-500 mb-1.5">
                    이름
                  </label>
                  <div className="relative">
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="appearance-none block w-full px-4 py-3 pl-11 border border-gray-300 rounded-xl placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                      placeholder="홍길동"
                    />
                    <User className="w-5 h-5 text-gray-400 absolute left-3.5 top-3.5" />
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-500 mb-1.5">
                  이메일
                </label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-4 py-3 pl-11 border border-gray-300 rounded-xl placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                    placeholder="user@example.com"
                  />
                  <Mail className="w-5 h-5 text-gray-400 absolute left-3.5 top-3.5" />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-500 mb-1.5">
                  비밀번호
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete={isLogin ? "current-password" : "new-password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-4 py-3 pl-11 pr-11 border border-gray-300 rounded-xl placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                    placeholder={isLogin ? "비밀번호" : "6자리 이상 입력"}
                  />
                  <Lock className="w-5 h-5 text-gray-400 absolute left-3.5 top-3.5" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-3.5 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {/* 비밀번호 강도 (회원가입만) */}
                {!isLogin && formData.password && (() => {
                  const strength = getPasswordStrength(formData.password);
                  return (
                    <div className="mt-2">
                      <div className="flex space-x-1 mb-1">
                        {[1, 2, 3, 4, 5].map(i => (
                          <div
                            key={i}
                            className={`h-1.5 flex-1 rounded-full ${i <= strength.score ? strength.color : 'bg-gray-200'}`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">{strength.label}</span>
                    </div>
                  );
                })()}
              </div>

              {!isLogin && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-500 mb-1.5">
                    비밀번호 확인
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="appearance-none block w-full px-4 py-3 pl-11 pr-11 border border-gray-300 rounded-xl placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                      placeholder="비밀번호 재입력"
                    />
                    <Lock className="w-5 h-5 text-gray-400 absolute left-3.5 top-3.5" />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3.5 top-3.5 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              )}

              {/* 에러 메시지 */}
              {error && (
                <div className="flex items-center space-x-2 text-red-600 text-sm bg-red-50 px-4 py-3 rounded-xl">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* 성공 메시지 */}
              {message && (
                <div className="text-green-700 text-sm text-center bg-green-50 px-4 py-3 rounded-xl">
                  {message}
                </div>
              )}

              {/* 약관 동의 체크박스 (회원가입만) */}
              {!isLogin && (
                <label className="flex items-start gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => { setAgreedToTerms(e.target.checked); if (error) setError(''); }}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-600 leading-relaxed">
                    <span className="text-blue-600 font-medium">[필수]</span>{' '}
                    <Link to="/terms" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">이용약관</Link>
                    {' '}및{' '}
                    <Link to="/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">개인정보 처리방침</Link>
                    에 동의합니다.
                  </span>
                </label>
              )}

              <div className="pt-1">
                <button
                  type="submit"
                  disabled={loading || (!isLogin && !agreedToTerms)}
                  className="w-full flex justify-center py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      처리 중...
                    </div>
                  ) : (
                    isLogin ? '로그인' : '회원가입'
                  )}
                </button>
              </div>
            </form>

            <div className="mt-6 space-y-3">
              {/* 로그인/회원가입 전환 */}
              <div className="text-center">
                <span className="text-sm text-gray-500">
                  {isLogin ? '계정이 없으신가요?' : '이미 계정이 있으신가요?'}
                </span>
                {' '}
                <button
                  type="button"
                  onClick={toggleMode}
                  className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                >
                  {isLogin ? '회원가입' : '로그인'}
                </button>
              </div>

              {/* 이메일/비밀번호 찾기 (로그인 화면에서만 표시) */}
              {isLogin && (
                <div className="text-center text-sm text-gray-500 space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-blue-600 hover:underline"
                  >
                    비밀번호 찾기
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={() => setShowFindEmail(true)}
                    className="text-blue-600 hover:underline"
                  >
                    이메일 찾기
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

      {/* 비밀번호 재설정 모달 */}
      <ForgotPasswordModal
        show={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />

      {/* 이메일 찾기 모달 */}
      <FindEmailModal
        show={showFindEmail}
        onClose={() => setShowFindEmail(false)}
      />
    </>
  );
};

export default LoginForm;
